import { useCallback, useMemo, useRef, useState } from 'react';
import { Message, ToolCall } from '../types/copilot';
import { startCopilotStream } from './useCopilotStream';

type UseCopilotArgs = {
  system?: string;
  tools?: string[]; // names exposed to model
};

export function useCopilot({ system, tools }: UseCopilotArgs) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [streaming, setStreaming] = useState(false);
  const streamRef = useRef<{ abort: () => void } | null>(null);

  const startNewAssistant = useRef(true);
  const tempAssistantId = useRef<string | null>(null);

  const clear = useCallback(() => {
    if (streamRef.current) streamRef.current.abort();
    setMessages([]);
    setStreaming(false);
  }, []);

  const startStream = useCallback(
    (snapshot: Message[]) => {
      if (streamRef.current) streamRef.current.abort();
      setMessages(snapshot);
      setStreaming(true);
      startNewAssistant.current = true;
      tempAssistantId.current = null;

      streamRef.current = startCopilotStream({
        system,
        messages: snapshot,
        tools,
        onDelta: (chunk) => {
          if (chunk?.error) {
            setStreaming(false);
            startNewAssistant.current = true;
            tempAssistantId.current = null;
            return;
          }
          if (chunk?.done) {
            setStreaming(false);
            startNewAssistant.current = true;
            tempAssistantId.current = null;
            return;
          }

          const delta = chunk?.choices?.[0]?.delta;
          if (!delta) return;

          setMessages((prev) => {
            const next = [...prev];

            if (delta.role === 'tool') {
              next.push({ id: crypto.randomUUID(), role: 'tool', content: delta.content || '', tool_call_id: delta.tool_call_id });
              startNewAssistant.current = true;
              return next;
            }

            const beginsAssistant =
              delta.role === 'assistant' || typeof delta.content === 'string' || !!delta.reasoning_content || !!delta.tool_calls;
            if (beginsAssistant && startNewAssistant.current) {
              const assistantMsg: Message = { id: crypto.randomUUID(), role: 'assistant', content: '', tool_calls: [] };
              next.push(assistantMsg);
              startNewAssistant.current = false;
              tempAssistantId.current = chunk?.id || assistantMsg.id || null;
            }

            const lastIndex = next.length - 1;
            if (lastIndex < 0) return next;
            const last = next[lastIndex];
            if (!last || last.role !== 'assistant') return next;

            const hasDeltaUpdates = Boolean(delta.tool_calls || typeof delta.reasoning_content === 'string' || typeof delta.content === 'string');
            if (!hasDeltaUpdates) return next;

            const updated: Message = { ...last };

            if (delta.tool_calls) {
              const toolCalls = (last.tool_calls || []).map((tc) => ({
                ...tc,
                function: { ...tc.function },
              }));
              (delta.tool_calls as ToolCall[]).forEach((tc: any) => {
                const idx = tc.index ?? 0;
                const existing = toolCalls[idx] || ({ type: 'function', function: { name: '', arguments: '' } } as any);
                const target = {
                  ...existing,
                  function: { ...existing.function },
                } as ToolCall;
                if (tc.id) (target as any).id = tc.id;
                if (tc.function?.name) target.function.name = tc.function.name;
                if (tc.function?.arguments) target.function.arguments = `${target.function.arguments || ''}${tc.function.arguments}`;
                toolCalls[idx] = target;
              });
              updated.tool_calls = toolCalls;
            }

            if (typeof delta.reasoning_content === 'string') {
              updated.reasoning_content = `${last.reasoning_content || ''}${delta.reasoning_content}`;
            }
            if (typeof delta.content === 'string') {
              updated.content = `${last.content || ''}${delta.content}`;
            }

            next[lastIndex] = updated;
            return next;
          });
        },
      });
    },
    [system, tools]
  );

  const send = useCallback(
    (text: string) => {
      const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content: text };
      const snapshot = [...messages, userMsg];
      startStream(snapshot);
    },
    [messages, startStream]
  );

  const regenerate = useCallback(() => {
    if (streaming) return;
    let lastUserIndex = -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') {
        lastUserIndex = i;
        break;
      }
    }
    if (lastUserIndex === -1) return;

    const snapshot = messages.slice(0, lastUserIndex + 1);
    const lastUser = snapshot[lastUserIndex];
    if (!lastUser?.content) return;

    startStream(snapshot);
  }, [messages, startStream, streaming]);

  const api = useMemo(() => ({ messages, streaming, send, regenerate, clear }), [messages, streaming, send, regenerate, clear]);
  return api;
}
